import type { StatusTone } from '../../theme';
import { pluralize } from '../../utils/format';

export interface EditorRecord {
  id: string;
  email: string;
  name: string;
  /** Raw from the API: 'active', 'pending', 'invited', 'accepted' or 'declined' (any case). */
  status: string;
}

/** Where an invitation stands, from the creator's side. */
export type InvitationState = 'active' | 'pending' | 'accepted' | 'declined';

export interface InvitationStatusMeta {
  state: InvitationState;
  label: string;
  tone: StatusTone;
}

/**
 * statusMeta has no invitation kind, so the chip takes these as label/tone
 * overrides. 'pending' (no PithPlay account yet) and 'invited' (an existing
 * account) are the same thing to the creator: sent, not yet accepted.
 * 'accepted' is a new editor who accepted and is finishing sign-up. Unknown
 * values read as pending, as the API itself normalises them.
 */
const INVITATION_STATUS: Record<string, InvitationStatusMeta> = {
  active: { state: 'active', label: 'Active', tone: 'done' },
  pending: { state: 'pending', label: 'Pending', tone: 'neutral' },
  invited: { state: 'pending', label: 'Pending', tone: 'neutral' },
  accepted: { state: 'accepted', label: 'Accepted', tone: 'active' },
  declined: { state: 'declined', label: 'Declined', tone: 'problem' },
};

export const getInvitationStatusMeta = (status?: string | null): InvitationStatusMeta =>
  INVITATION_STATUS[String(status || '').trim().toLowerCase()] ?? INVITATION_STATUS.pending;

/** Anyone who hasn't finished joining may need the invitation again. */
export const canResendInvitation = (status?: string | null): boolean =>
  getInvitationStatusMeta(status).state !== 'active';

export interface EditorSummary {
  total: number;
  active: number;
  pending: number;
  accepted: number;
  declined: number;
  /** Invitations left before the limit. Every editor row counts, as the invite check does. */
  left: number;
}

export const summarizeEditors = (editors: EditorRecord[], limit: number): EditorSummary => {
  const summary: EditorSummary = { total: editors.length, active: 0, pending: 0, accepted: 0, declined: 0, left: 0 };
  for (const editor of editors) {
    summary[getInvitationStatusMeta(editor.status).state] += 1;
  }
  summary.left = Math.max(0, limit - editors.length);
  return summary;
};

/**
 * "3 editors · 2 active · 1 pending · 22 of 25 invites left". Statuses with
 * no one in them are left out, so the parts always add up to the total.
 */
export const formatEditorSummary = (summary: EditorSummary, limit: number): string => {
  if (summary.total === 0) return `You can invite up to ${pluralize(limit, 'editor')}.`;
  const parts = [pluralize(summary.total, 'editor')];
  if (summary.active) parts.push(`${summary.active} active`);
  if (summary.pending) parts.push(`${summary.pending} pending`);
  if (summary.accepted) parts.push(`${summary.accepted} accepted`);
  if (summary.declined) parts.push(`${summary.declined} declined`);
  parts.push(summary.left === 0 ? 'no invites left' : `${summary.left} of ${limit} invites left`);
  return parts.join(' · ');
};

/** True when no more editors can be added (every row counts against the limit). */
export const isAtEditorLimit = (count: number, limit: number): boolean => count >= limit;

/**
 * The link the invitation email carries: new people register, people who
 * already have an account accept. Built exactly as before the redesign.
 */
export const buildInvitationLink = (
  invitationStatus: string | undefined,
  invitationToken: string | undefined,
  email: string,
): string => {
  const base = process.env.REACT_APP_APP_URL || 'https://app.pithplay.com';
  const path = invitationStatus === 'pending' ? 'editor-registration' : 'editor-invitation';
  return `${base}/${path}?token=${invitationToken}&email=${encodeURIComponent(email)}`;
};

// Wording that only makes sense to a developer (network failures, masked
// server errors, crash text). Never shown; the caller's fallback is.
const TECHNICAL_ERROR =
  /failed to fetch|networkerror|network request failed|load failed|response not successful|status code|unexpected error|internal server error|typeerror|referenceerror|cannot read|undefined|null|graphql|json|exception|token/i;

/**
 * The API's own sentence when it is written for people (for example a rate
 * limit, or "You cannot invite yourself"), otherwise `fallback`.
 */
export const readableEditorError = (error: unknown, fallback: string): string => {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const message = String(raw || '').split('\n')[0].trim();
  if (!message || TECHNICAL_ERROR.test(message)) return fallback;
  return message;
};

/** The client-side self-invitation check, and the API's version of it. */
export const isSelfInvitationError = (error: unknown): boolean => {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /cannot invite yourself/i.test(String(raw || ''));
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateInviteEmail = (email: string, ownEmail?: string | null): string | null => {
  const value = email.trim();
  if (!value) return 'Enter an email address.';
  if (!EMAIL_PATTERN.test(value)) return 'Enter a valid email address, like name@example.com.';
  if (ownEmail && ownEmail.trim().toLowerCase() === value.toLowerCase()) return "You can't invite yourself.";
  return null;
};
